/**
 * User Profile Store
 * 
 * Fetches user status early on page load and provides replayable subscription.
 * Emits events along the lifecycle for progressive UI updates.
 */

export type UserStage = 'unknown' | 'checking' | 'authenticated' | 'ready' | 'error';

/** Membership object from MemberPress */
export interface Membership {
  id: number;
  title: string;
}

/** All possible capability IDs - keep in sync with capabilities.json */
export type CapabilityId = 
  | 'canViewMaps'
  | 'canUseFavorites'
  | 'canUseLocationFavorites'
  | 'canExportLocationPdf'
  | 'canExportChecklistPdf'
  | 'canUseChecklists'
  | 'canCustomizeChecklists'
  | 'hasAdFreeExperience'
  | 'canViewVideos'
  | 'canViewAdFreeVideos'
  | 'canViewPaidVideos'
  | 'canAccessPremiumContent'
  | 'canCreateUserLists';

/** Map of capability ID to boolean */
export type Capabilities = Record<CapabilityId, boolean>;

/** Capability requirement info for upgrade modals */
export interface CapabilityRequirement {
  label: string;
  description: string;
  requiresAuth: boolean;
  requiresAnyMembership: boolean;
  membershipNames: string[];
}

export interface UserState {
  stage: UserStage;
  loggedIn: boolean;
  userId?: number;
  userName?: string;
  userEmail?: string;
  avatarUrl?: string;
  /** User's active MemberPress memberships */
  memberships: Membership[];
  capabilities: Partial<Capabilities>;
  /** Capability requirements for upgrade modals */
  capabilityRequirements: Record<string, CapabilityRequirement>;
  nonce?: string; // For authenticated API calls (rd_login_popout)
  restNonce?: string; // For REST API calls (wp_rest)
  isAdmin?: boolean;
}

type UserSubscriber = (state: UserState) => void;

const DEFAULT_STATE: UserState = {
  stage: 'unknown',
  loggedIn: false,
  memberships: [],
  capabilities: {},
  capabilityRequirements: {},
};

class UserStore {
  private state: UserState = { ...DEFAULT_STATE };
  private subscribers = new Set<UserSubscriber>();
  private fetchPromise: Promise<UserState> | null = null;

  getState(): UserState {
    return { ...this.state };
  }

  /** Subscribe to user state changes - replays current state immediately */
  subscribe(fn: UserSubscriber): () => void {
    this.subscribers.add(fn);
    fn(this.state);
    return () => {
      this.subscribers.delete(fn);
    };
  }

  private setState(partial: Partial<UserState>): void {
    this.state = { ...this.state, ...partial };
    this.emit();
    this.notifySubscribers();
  }

  private emit(): void {
    document.dispatchEvent(new CustomEvent('rd:user:changed', { 
      detail: this.state 
    }));
  }

  private notifySubscribers(): void {
    for (const fn of this.subscribers) {
      fn(this.state);
    }
  }

  /** Start fetching user data - returns promise that resolves when complete */
  fetch(): Promise<UserState> {
    if (this.fetchPromise) return this.fetchPromise;

    this.setState({ stage: 'checking' });

    this.fetchPromise = this.doFetch();
    return this.fetchPromise;
  }

  private async doFetch(): Promise<UserState> {
    const restUrl = this.getRestUrl();

    try {
      // Step 1: Check if logged in
      const statusRes = await fetch(`${restUrl}user-status`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!statusRes.ok) throw new Error('Failed to fetch user status');

      const status = await statusRes.json();

      if (!status.loggedIn) {
        this.setState({ 
          stage: 'ready', 
          loggedIn: false, 
          memberships: [],
          capabilities: status.capabilities ?? {},
          capabilityRequirements: status.capabilityRequirements ?? {},
        });
        return this.state;
      }

      // User is logged in - emit intermediate state and event for UI updates
      this.setState({ stage: 'authenticated', loggedIn: true });
      document.dispatchEvent(new CustomEvent('rd:user-authenticated', {
        detail: { user: this.getState() },
      }));

      // Step 2: Fetch full user data
      if (status.nonce) {
        const userData = await this.fetchUserData(restUrl, status.nonce);

        // Get capabilities and memberships from server response
        const capabilities = status.capabilities ?? {};
        const memberships: Membership[] = status.memberships ?? [];
        
        this.setState({
          stage: 'ready',
          loggedIn: true,
          userId: userData?.userId,
          userName: userData?.userName,
          userEmail: userData?.userEmail,
          avatarUrl: userData?.avatarUrl,
          memberships,
          capabilities,
          capabilityRequirements: status.capabilityRequirements ?? {},
          nonce: status.nonce,
          restNonce: status.restNonce,
          isAdmin: status.isAdmin ?? false,
        });
      } else {
        this.setState({ 
          stage: 'ready', 
          loggedIn: true, 
          memberships: [],
          capabilities: {},
          capabilityRequirements: {},
        });
      }

      return this.state;
    } catch (error) {
      console.error('[UserStore] Fetch error:', error);
      this.setState({ stage: 'error', loggedIn: false, memberships: [] });
      return this.state;
    }
  }

  private async fetchUserData(restUrl: string, nonce: string): Promise<Partial<UserState> | null> {
    try {
      const res = await fetch(`${restUrl}user-data?nonce=${encodeURIComponent(nonce)}`, {
        credentials: 'same-origin',
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  private getRestUrl(): string {
    const config = (window as any).rdLoginPopout;
    if (config?.restUrl) return config.restUrl;
    return `${window.location.origin}/wp-json/rd-womoguide/v1/`;
  }

  /** Force refresh user data */
  refresh(): Promise<UserState> {
    this.fetchPromise = null;
    this.state = { ...DEFAULT_STATE };
    return this.fetch();
  }

  /** Check if user has a specific capability */
  can(capability: CapabilityId): boolean {
    return this.state.capabilities[capability] === true;
  }

  /** Get all capabilities */
  getCapabilities(): Partial<Capabilities> {
    return { ...this.state.capabilities };
  }
}

export const userStore = new UserStore();

/** Convenience function to check capability */
export function userCan(capability: CapabilityId): boolean {
  return userStore.can(capability);
}
